// Vario-Slot: products.articles.detail.tabs.listings.detail.{VARIO_APP_ID}
//
// Zwei Pfade analog der alten App + UX-Wunsch 2026-05-20:
//   A) Aufschalten — EAN-Suche im Kaufland-Katalog
//      → Treffer: User uebernimmt das bestehende Angebot mit der Katalog-Kategorie
//      → Kein Treffer: Wechsel zu B
//   B) Neu listen — Decide/Vorschlaege oder Auswahl aus der eigenen Kategorieliste
//      (kaufland.tenant_categories, Task 7), dann Assign. Die frueher hier verbaute
//      Volltextsuche ueber alle 6.087 Kategorien ist seit Task 8 entfernt.
//
// Beide Pfade enden in: PATCH /erp/articles/{aid}/listings/{lid} mit
// categoryRefs[0] = <vario-cat-id> (resolved via externalReference). Damit
// taucht das Listing im naechsten Inbox-Scan auf.
//
// Kategorien aus EAN-Treffer (A) oder Kauflands Vorschlagsdienst (B), die nicht in der
// eigenen Liste stehen, werden von stelleKategorieSicher() still mit added_by='auto'
// aufgenommen -- nur der dritte, frueher manuelle Weg wurde ersetzt.
//
// URL-Params (Vario): id = articleId, listingId = listingId.

import type { SupabaseClient } from '@supabase/supabase-js';
import { renderListingDetailPane, STATUS_PILL } from './shared/listing-detail-pane.js';
import type { ListingStatus } from './shared/listing-detail-pane.js';
import { ladeAlleSeiten, ladeGewaehlteKategorien } from '../lib/category-path.js';
import { bestimmeSlotKnoepfe } from '../lib/slot-mode-buttons.js';
import { awaitJob } from '../lib/await-job.js';
import {
  aufnahmeFehlerText,
  erkenneKategorieSyncFehler,
  kategorieIdsFuerAufnahme,
  pushZeile,
  uebernahmeFrage,
  zielKanaeleFuerPush,
  type KanalWahl,
} from '../lib/kategorie-uebernahme.js';
import { describeJobFailure } from '../lib/job-failure.js';
import { matchedOfferZeilen, zeigeMatchedKarte, type MatchedOffer } from '../lib/matched-offer.js';
import type { GewaehlteKategorie } from '../lib/category-path.js';
import type { VererbungsErgebnis } from '../lib/category-modal.js';
import { KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';

/** Wie lange der Slot auf den Kategorien-Sync nach Vario wartet. Ein Knoten samt Elternkette
 *  braucht Sekunden (2 parallele POSTs, 429-Backoff im Worker); der Rest ist Puffer fuer eine
 *  belegte Queue. Danach bleibt der Job in den Einstellungen sichtbar und laeuft weiter. */
const SYNC_WARTEZEIT_MS = 180_000;

interface SlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

interface LoadResponse {
  storefront: string;
  salesChannelId: string;
  current: { varioCategoryId: string; label: string | null; kauflandCategoryId: number | null } | null;
  decideInput: {
    title: string;
    description: string;
    ean: string | null;
    /** Anzeigename des Herstellers, null ohne Label. */
    manufacturer: string | null;
    /** Hersteller-Konto am Artikel verknuepft. Optional, weil ein aelteres Backend das Feld
     *  noch nicht liefert -- dann prueft der Slot ueber `manufacturer` (slot-mode-buttons.ts). */
    hasManufacturer?: boolean;
    price: number;
  };
  /** Vario-SKU des Artikels (article.articleNumber). Wird beim Aufschalten-Upsert
   *  als Kaufland-Pflichtfeld id_offer mitgeschickt. null wenn der Vario-Artikel
   *  noch keine Nummer hat. */
  articleNumber: string | null;
  /** Angebots-Zuordnung (Angebote-Tab): Mapping dieses Listings auf eine Kaufland-Unit samt
   *  Unit-Daten. Optional, weil ein aelteres Backend das Feld nicht liefert. null = keine. */
  matchedOffer?: MatchedOffer | null;
  /** true wenn der Vario-Artikel ein Variantenhauptartikel ist
   *  (productType='VARIANT_MAIN_ARTICLE'). Wandert beim Upsert in
   *  kaufland.listings.is_variant_parent. */
  isVariantParent?: boolean;
  hasCacheForStorefront: boolean;
}

interface FindByEanResponse {
  found: boolean;
  ean: string;
  storefront: string;
  idProduct?: string;
  idCategory?: number | null;
  title?: string | null;
  productUrl?: string | null;
  category?: { id: number | null; name: string | null; title_singular: string | null; level?: number | null } | null;
}

interface DecideResponse {
  suggestions: Array<{
    kauflandCategoryId: number;
    confidence: number | null;
    name: string | null;
    title_singular: string | null;
    level: number | null;
  }>;
}

/** Eintrag der eigenen, vom Haendler in den Einstellungen gepflegten Kategorieliste
 *  (Task 7). Ersetzt seit Task 8 die Volltextsuche ueber alle 6.087 Kaufland-Kategorien
 *  im "Neu listen"-Pfad -- der Haendler waehlt nur noch aus seiner kurzen Liste.
 *  `name` ist seit Fix-Runde 2 der fertige Anzeigename inkl. direkter Elternkategorie
 *  ("Elternname › Name"), noetig weil Blattnamen in einigen Storefronts mehrfach
 *  vergeben sind (siehe ladeEigeneKategorien). */
interface EigeneKategorie {
  id: number;
  name: string;
}

/** Gueltige GTIN: 8/12/13/14 Ziffern + GS1-Mod-10-Pruefziffer. Gleiche Logik wie in
 *  der Edge Function kaufland-find-product-by-ean-v2 — Kaufland lehnt alles andere
 *  mit 400 "Invalid EAN provided" ab, deshalb pruefen wir vor dem Call und zeigen
 *  sofort eine verstaendliche Meldung. */
function isValidGtin(ean: string): boolean {
  if (!/^\d+$/.test(ean)) return false;
  if (![8, 12, 13, 14].includes(ean.length)) return false;
  let sum = 0;
  for (let i = 0; i < ean.length - 1; i += 1) {
    const digit = ean.charCodeAt(ean.length - 2 - i) - 48;
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === ean.charCodeAt(ean.length - 1) - 48;
}

const STOREFRONT_LABELS: Record<string, string> = {
  de: 'Deutschland', at: 'Österreich', cz: 'Tschechien', sk: 'Slowakei',
  pl: 'Polen', fr: 'Frankreich', it: 'Italien',
};

/**
 * Laedt die vom Haendler in den Einstellungen gepflegte Kategorieliste (Task 7): erst die
 * gewaehlten category_id aus kaufland.tenant_categories, danach deren Anzeigenamen aus
 * kaufland.categories.
 *
 * Fix-Runde 1, Befund 2: NICHT mehr ueber ladeKategorieKatalog() (kompletter
 * Storefront-Katalog, 6.087 Zeilen / ~700 KB) wie urspruenglich in Task 8 gebaut --
 * der Slot zeigt nur die Handvoll eigener Kategorien EINES Haendlers, dafuer reicht
 * eine gezielte `.in('category_id', ids)`-Abfrage auf genau diese IDs. Der
 * Brotkrumen-Pfad (kategorienPfad/ladeKategorieKatalog aus ../lib/category-path.js,
 * noetig weil `path` bei allen 6.087 Zeilen NULL ist) bleibt den Einstellungen
 * vorbehalten, wo tatsaechlich ueber alle 6.087 Zeilen gesucht wird.
 *
 * Fix-Runde 2: Reiner Kategoriename reicht NICHT immer -- in pl/fr/cz/sk gibt es
 * mehrfach vergebene Blattnamen (z. B. dreimal "Podnozek" in pl unter verschiedenen
 * IDs), zwei davon in derselben Liste waeren sonst ununterscheidbar. Deshalb zeigt der
 * Anzeigename zusaetzlich die DIREKTE Elternkategorie ("Elternname › Name"), aufgeloest
 * ueber EINE weitere gezielte Abfrage nur auf die tatsaechlich vorkommenden
 * parent_id-Werte -- bewusst nur eine Ebene, keine volle Vorfahrenkette und kein
 * Vollkatalog, um die Ersparnis aus Fix-Runde 1 zu erhalten. Fehlt die Elternkategorie
 * (NULL oder nicht gefunden), bleibt es beim reinen Namen ohne Platzhalter/Trenner.
 *
 * Eine leere Liste heisst: der Haendler hat noch keine eigene Kategorie ausgewaehlt --
 * renderEigeneKategorien() zeigt dafuer einen Hinweis statt eines leeren Dropdowns.
 *
 * Task 6 (2026-08-09): Die Auswahl ist seit Migration 0043 (urspruenglich als 0040 angelegt, wegen einer Nummernkollision umbenannt) je Storefront getrennt
 * (Schluessel (tenant_id, storefront, category_id) statt (tenant_id, category_id)) -- ein
 * Haendler verkauft in Polen andere Kategorien als in Deutschland. Die Abfrage MUSS deshalb
 * nach storefront filtern, sonst mischen sich die Auswahllisten aller Marktplaetze im
 * Dropdown eines einzelnen Listings.
 */
async function ladeEigeneKategorien(ctx: SlotCtx, storefront: string): Promise<EigeneKategorie[]> {
  // Geblaettert ueber ladeGewaehlteKategorien: PostgREST kappt bei max_rows = 1000 lautlos,
  // und eine abgeschnittene Liste sieht hier aus wie "der Haendler hat die Kategorie nicht
  // ausgewaehlt". Dieselbe Falle wie im Katalog-Lader, deshalb dieselbe Hilfsfunktion.
  const ids = (await ladeGewaehlteKategorien(ctx.supabase, storefront)).map((r) => r.categoryId);
  if (ids.length === 0) return [];

  type KategorieRow = { category_id: number; parent_id: number | null; name: string | null; title_singular: string | null };
  // Auch die Namensaufloesung blaettert: Sie liefert genau eine Zeile je gewaehlter Kategorie,
  // haette also ab 1001 Auswahlzeilen dasselbe Limit getroffen. Die Kategorien ohne Namen
  // waeren dann als nackte ID im Dropdown gelandet. `order` ist fuer das Blaettern zwingend.
  const rows = await ladeAlleSeiten<KategorieRow>((von, bis) =>
    ctx.supabase
      .schema('kaufland')
      .from('categories')
      .select('category_id,parent_id,name,title_singular')
      .eq('storefront', storefront)
      .in('category_id', ids)
      .order('category_id', { ascending: true })
      .range(von, bis),
  );

  const eigene = new Map<number, { name: string; parentId: number | null }>();
  for (const r of rows) {
    eigene.set(Number(r.category_id), {
      name: r.title_singular ?? r.name ?? String(r.category_id),
      parentId: r.parent_id != null ? Number(r.parent_id) : null,
    });
  }

  // Nur die Eltern-IDs nachladen, die wir nicht ohnehin schon kennen (z. B. weil die
  // Elternkategorie selbst zur eigenen Liste gehoert).
  const fehlendeElternIds = Array.from(new Set(
    [...eigene.values()]
      .map((e) => e.parentId)
      .filter((id): id is number => id != null && !eigene.has(id)),
  ));
  const elternNamen = new Map<number, string>();
  if (fehlendeElternIds.length > 0) {
    type ElternRow = { category_id: number; name: string | null; title_singular: string | null };
    const elternRows = await ladeAlleSeiten<ElternRow>((von, bis) =>
      ctx.supabase
        .schema('kaufland')
        .from('categories')
        .select('category_id,name,title_singular')
        .eq('storefront', storefront)
        .in('category_id', fehlendeElternIds)
        .order('category_id', { ascending: true })
        .range(von, bis),
    );
    for (const r of elternRows) {
      elternNamen.set(Number(r.category_id), r.title_singular ?? r.name ?? String(r.category_id));
    }
  }

  const eintraege = ids.map((id) => {
    const eigeneRow = eigene.get(id);
    const leafName = eigeneRow?.name ?? String(id);
    const parentId = eigeneRow?.parentId ?? null;
    const parentName = parentId != null ? (eigene.get(parentId)?.name ?? elternNamen.get(parentId)) : undefined;
    return { id, leafName, displayName: parentName ? `${parentName} › ${leafName}` : leafName };
  });

  // Sortierschluessel ist der Blattname, NICHT der Anzeigename -- so stehen gleichnamige
  // Eintraege (unterschiedliche Elternkategorie) direkt nebeneinander, genau dort, wo der
  // Unterschied auffallen soll.
  eintraege.sort((a, b) =>
    a.leafName.localeCompare(b.leafName) ||
    a.displayName.localeCompare(b.displayName) ||
    a.id - b.id,
  );

  return eintraege.map((e) => ({ id: e.id, name: e.displayName }));
}

export async function renderListingStatus(ctx: SlotCtx) {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = renderShell();

  const params = readSlotParams();
  if (!params.articleId || !params.listingId) {
    setStatusBar(ctx.contentEl, 'error', `URL-Parameter unvollstaendig. articleId=${params.articleId ?? '∅'}, listingId=${params.listingId ?? '∅'}`);
    return;
  }

  const supabaseUrl = ctx.supabaseUrl;

  let state: LoadResponse | null = null;
  let mode: 'choice' | 'lookup' | 'new-listing' = 'choice';
  // Wenn der User auf „Kategorie aendern" klickt, ueberbruecken wir die Ready-Box
  // und zeigen wieder die Choice-Cards, obwohl state.current gesetzt ist.
  let forceChoice = false;
  // Status der zugehoerigen Inbox-Row in kaufland.listings — null wenn das Listing noch
  // nicht in unserer Inbox steht (Slot-Direkt-Upsert ist noch nicht gelaufen, oder es
  // ist archived). Steuert zusammen mit state.current ob der Configure-Block angezeigt wird.
  let inboxStatus: string | null = null;
  // PK der Inbox-Row in kaufland.listings — braucht das eingebettete Detail-Pane.
  let inboxListingId: string | null = null;
  // Kauflands letzte Rueckmeldung zur laufenden Pruefung (kaufland_status_reason) und die
  // letzte Fehlermeldung. Beide nur fuer die Statuszeile in „Aktuell zugeordnet".
  //
  // Warum das hier sein muss: Nach einem Klick auf „Listen" passierte im Slot sichtbar
  // NICHTS. Der Job lief, das Listing wechselte auf 'queued' und spaeter 'validating',
  // aber der Slot zeigte weiter nur die Kategorie. Der Status wurde sogar schon geladen
  // (loadInboxStatus), nur nie gezeichnet.
  let inboxStatusReason: string | null = null;
  let inboxLastError: string | null = null;
  // Hier stand `isLiveOnKaufland` samt `loadLiveStatus()`. Beides ist am 2026-08-17 mit der
  // Box „Aktuell zugeordnet" entfallen: Die Angabe steht jetzt in der Kategorie-Leiste des
  // Detail-Fensters, und zwar genauer — `listingPill` unterscheidet „Gelistet", „Pausiert"
  // und „Gelistet, ohne Bestand", wo hier nur „laufendes Angebot" stand. Mit der Pille ist
  // auch die zusaetzliche Unit-Abfrage beim Oeffnen des Slots weggefallen.
  // Letztes EAN-Lookup-Ergebnis — wird in doLookup() gesetzt und in doAssign()
  // ausgewertet, um den Attach-Modus (Aufschalten auf existing id_product) zu
  // erkennen. Bleibt null im Neu-Listen-Pfad und nach Verlassen des Lookup-Modus.
  let lookupResult: FindByEanResponse | null = null;
  // Cache der eigenen Kategorieliste fuer den "Neu listen"-Pfad -- null heisst "noch nicht
  // geladen". stelleKategorieSicher() setzt ihn nach einer automatischen Aufnahme zurueck,
  // damit der naechste Besuch von "Neu listen" die neue Kategorie sofort zeigt.
  let eigeneKategorien: EigeneKategorie[] | null = null;
  // Aufloeser einer offenen Kanal-Frage (frageKanalwahl). Ein zweiter Zuweisungsklick waehrend
  // die Frage steht, loest die alte mit null auf, sonst hinge ihr Promise fuer immer und der
  // erste Knopf bliebe gesperrt (Review-Fund 2026-09-13). Hier oben deklariert, nicht neben
  // der Funktion: `let` ist bis zur Deklaration in der TDZ, und die Handler laufen frueher.
  let offeneFrage: ((wahl: KanalWahl | null) => void) | null = null;

  await load();

  // ---------------------------------------------------------------------
  async function load(): Promise<{ ok: true } | { ok: false; code?: string | undefined }> {
    setStatusBar(ctx.contentEl, 'loading', 'Lade Listing-Daten …');
    const res = await callFn<LoadResponse>('vario-listing-category', {
      action: 'load',
      articleId: params.articleId,
      listingId: params.listingId,
    });
    if (!res.ok) {
      setStatusBar(ctx.contentEl, 'error', `Konnte Listing nicht laden: ${res.error}`);
      return { ok: false, code: res.code };
    }
    state = res.value;
    setSubtitle(`${(STOREFRONT_LABELS[state.storefront] ?? state.storefront).toUpperCase()} · ${state.storefront.toUpperCase()}${state.hasCacheForStorefront ? '' : ' · ⚠ Kategorien-Cache leer'}`);
    setStatusBar(ctx.contentEl, '', '');
    await loadInboxStatus();
    renderModeUi();
    return { ok: true };
  }

  async function loadInboxStatus() {
    // RLS scoped via active_tenant_id-Claim — wir bekommen nur Rows des eigenen Tenants.
    const { data, error } = await ctx.supabase
      .schema('kaufland')
      .from('listings')
      .select('id, status, kaufland_status_reason, last_error')
      .eq('vario_listing_id', params.listingId!)
      .maybeSingle();
    if (error) {
      console.warn('[listing-status] inbox lookup failed:', error.message);
      inboxStatus = null;
      inboxListingId = null;
      inboxStatusReason = null;
      inboxLastError = null;
      return;
    }
    const row = data as {
      id: string;
      status: string;
      kaufland_status_reason: string | null;
      last_error: string | null;
    } | null;
    inboxStatus = row?.status ?? null;
    inboxListingId = row?.id ?? null;
    inboxStatusReason = row?.kaufland_status_reason ?? null;
    inboxLastError = row?.last_error ?? null;
  }

  function renderModeUi() {
    const choice = ctx.contentEl.querySelector<HTMLElement>('#mode-choice')!;
    const configure = ctx.contentEl.querySelector<HTMLElement>('#mode-configure')!;
    const lookup = ctx.contentEl.querySelector<HTMLElement>('#mode-lookup')!;
    const newL = ctx.contentEl.querySelector<HTMLElement>('#mode-new-listing')!;

    // Configure-Modus: Kategorie ist im Vario-Listing zugeordnet UND der Artikel steht in
    // unserer Inbox (kaufland.listings, nicht archived) UND der User hat nicht „aendern"
    // gewaehlt. Ohne Inbox-Row faellt der Slot in die alten Choice-Cards zurueck — sonst
    // wuerde das Configure-Pane auch fuer Artikel angezeigt, die der Lister gar nicht
    // kennt (z.B. wenn der Scan-Job aktuell nicht durchlaeuft).
    const inInbox = inboxStatus !== null && inboxStatus !== 'archived';
    // Configure-Pane: Posteingangszeile UND (Kategorie am Vario-Listing ODER Zeile schon
    // gelistet). Der zweite Zweig (2026-09-13) gilt fuer Angebots-Zuordnungen: Der
    // Zuordnungs-Job schreibt eine attach/published-Zeile, die Kategorie am Vario-Listing
    // ist dort nicht immer aufloesbar, und bei „attach" kommt sie ohnehin von Kaufland.
    const showConfigure = mode === 'choice' && inInbox && !forceChoice
      && (Boolean(state?.current) || inboxStatus === 'published');
    // Angebots-Zuordnung ohne Posteingangszeile (Angebote-Tab, 2026-09-13): Karte statt Wahl.
    // Das Configure-Pane gewinnt, weil ein ueber den Slot aufgeschaltetes Listing nach dem
    // Push ebenfalls ein Mapping traegt und dort der Lister zustaendig ist.
    const showMatched = zeigeMatchedKarte({ hatMatch: Boolean(state?.matchedOffer), mode, showConfigure, forceChoice });
    choice.hidden = mode !== 'choice' || showConfigure || showMatched;
    configure.hidden = !showConfigure;
    const matchedEl = ctx.contentEl.querySelector<HTMLElement>('#mode-matched')!;
    matchedEl.hidden = !showMatched;
    if (showMatched) renderMatchedKarte(matchedEl);
    // Der Knopf sitzt in der Kopfzeile, gehoert aber zum Configure-Modus: gleiche
    // Bedingung wie das Pane, damit er nicht ueber Ansichten stehen bleibt, in denen es
    // keine zugeordnete Kategorie zu aendern gibt.
    const reassign = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-reassign')!;
    reassign.hidden = !showConfigure;
    lookup.hidden = mode !== 'lookup';
    newL.hidden = mode !== 'new-listing';

    // Knopf-Regel (2026-09-13): Aufschalten braucht die EAN, Neu listen EAN UND Hersteller.
    // EINMAL hier berechnet, weil „Neu listen" an zwei Stellen erreichbar ist: auf der
    // Choice-Karte und als „Doch neu listen" im Lookup nach einem Kein-Treffer. Review-Fund:
    // Der zweite Weg war ungesperrt und ist bei fehlendem Hersteller der wahrscheinlichste
    // (EAN da, Kaufland kennt den Artikel nicht). Bis dahin war „Neu listen" nirgends
    // gesperrt; ein fehlender Hersteller war nur ein Hinweis, und Kaufland haette das
    // Pflichtattribut `manufacturer` leer bekommen.
    const knoepfe = aktuelleKnoepfe();
    const lookupNewBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-new-instead')!;
    lookupNewBtn.disabled = !knoepfe.newListing.enabled;
    lookupNewBtn.title = knoepfe.newListing.title;

    // Der Lister ist vollständig hier im Artikel-Slot verfügbar (Configure-Pane). Der
    // frühere "Im Lister öffnen"-Sprung auf die navigation.root-Seite `kaufland-page`
    // wurde 2026-06-14 entfernt: navigation.root ist abgebaut, der Lister ist ein
    // eigener Verkaufskanal-Tab (/#/list). Kein Cross-Window-Deep-Link mehr nötig.
    if (showConfigure) {
      mountConfigurePane();
    }

    if (mode === 'choice') {
      const eanLine = ctx.contentEl.querySelector<HTMLElement>('#choice-ean')!;
      const noEan = ctx.contentEl.querySelector<HTMLElement>('#choice-no-ean')!;
      const searchBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup')!;
      const choiceMan = ctx.contentEl.querySelector<HTMLElement>('#choice-manufacturer')!;
      // Variantenvater: in Vario traegt der Hauptartikel KEINE EAN — die EANs liegen an
      // den Kind-Varianten. Der Vater wird bei Kaufland auch nicht selbst als Produkt
      // gelistet (jede Variante ist ein eigenes Kaufland-Produkt mit eigener EAN), er ist
      // nur der Einstieg fuer die Varianten-Anlage. Deshalb ist die fehlende EAN hier kein
      // Fehler, und "Aufschalten per EAN-Suche" ist gegenstandslos.
      const isParent = state?.isVariantParent === true;
      const parentCard = ctx.contentEl.querySelector<HTMLElement>('#choice-card-attach')!;
      const parentNote = ctx.contentEl.querySelector<HTMLElement>('#choice-variant-parent-note')!;
      parentCard.hidden = isParent;
      parentNote.hidden = !isParent;
      const newBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-new-listing')!;
      if (state?.decideInput.ean) {
        eanLine.textContent = state.decideInput.ean;
      } else if (isParent) {
        eanLine.textContent = '— (liegt an den Varianten)';
      } else {
        eanLine.textContent = '—';
      }
      noEan.hidden = !knoepfe.eanFehlt;
      searchBtn.disabled = !knoepfe.attach.enabled;
      searchBtn.title = knoepfe.attach.title;
      newBtn.disabled = !knoepfe.newListing.enabled;
      newBtn.title = knoepfe.newListing.title;
      // Konto verknuepft, aber ohne Label: sagen, statt „—" zu zeigen und keinen Hinweis.
      choiceMan.textContent = state?.decideInput.manufacturer
        ?? (state?.decideInput.hasManufacturer === true ? '(Hersteller ohne Bezeichnung)' : '—');
      const manWarn = ctx.contentEl.querySelector<HTMLElement>('#choice-no-manufacturer')!;
      manWarn.hidden = !knoepfe.herstellerFehlt;
    }
    if (mode === 'new-listing') {
      renderInputPreview();
      void renderEigeneKategorien();
    }
  }

  /**
   * Karte „Aufgeschaltet ueber Angebots-Zuordnung" (2026-09-13). Zeichnet nur neu, wenn sich die
   * Unit aendert: renderModeUi laeuft bei jedem Moduswechsel, und ein Neuzeichnen wuerde den
   * zweistufigen Aufheben-Klick zuruecksetzen.
   */
  function renderMatchedKarte(host: HTMLElement): void {
    const o = state?.matchedOffer;
    if (!o) { host.innerHTML = ''; delete host.dataset['unit']; return; }
    if (host.dataset['unit'] === o.idUnit) return;
    host.dataset['unit'] = o.idUnit;
    const zeilen = matchedOfferZeilen(o)
      .map((z) => `<tr><th style="text-align:left;white-space:nowrap;padding-right:12px;">${escapeHtml(z.label)}</th><td>${escapeHtml(z.wert)}</td></tr>`)
      .join('');
    host.innerHTML = `
      <div class="kls-meta">
        <div class="kls-choice-title">Aufgeschaltet über Angebots-Zuordnung</div>
        <div class="kls-choice-desc" style="margin:4px 0 10px;">
          Dieses Vario-Listing ist einem bestehenden Kaufland-Angebot zugeordnet (Angebote-Tab).
          Preis und Bestand laufen über die Zuordnung, die Produktdaten pflegt Kaufland.
        </div>
        <table class="v-table" style="width:100%;font-size:0.9em;"><tbody>${zeilen}</tbody></table>
        <div class="kls-actions-row" style="justify-content:flex-start;">
          <button type="button" class="kls-btn kls-btn--secondary" id="btn-unassign">Zuordnung aufheben</button>
        </div>
        <div id="unassign-confirm" hidden class="kls-meta" style="margin-top:8px;">
          <div>
            Aufheben entfernt den Kaufland-Verkaufskanal am Vario-Artikel (kann Vario ihn nicht löschen, wird er deaktiviert)
            und löscht die Zuordnung. Das Kaufland-Angebot selbst bleibt bestehen; Preis und Bestand werden dann nicht mehr
            aus Vario gepflegt. Dieses Fenster gehört zum Verkaufskanal und schließt sich damit inhaltlich ab.
          </div>
          <div class="kls-actions-row" style="justify-content:flex-start;">
            <button type="button" class="kls-btn kls-btn--danger" id="btn-unassign-confirm">Ja, Zuordnung aufheben</button>
            <button type="button" class="kls-btn kls-btn--secondary" id="btn-unassign-cancel">Abbrechen</button>
          </div>
        </div>
      </div>`;
    const confirmBox = host.querySelector<HTMLElement>('#unassign-confirm')!;
    // Zweistufiger Klick statt window.confirm (projektweit verboten): der zweite Knopf IST die
    // Bestaetigung, die Folgen stehen direkt daneben.
    host.querySelector<HTMLButtonElement>('#btn-unassign')!.addEventListener('click', () => { confirmBox.hidden = false; });
    host.querySelector<HTMLButtonElement>('#btn-unassign-cancel')!.addEventListener('click', () => { confirmBox.hidden = true; });
    host.querySelector<HTMLButtonElement>('#btn-unassign-confirm')!.addEventListener('click', (e) => {
      void doUnassign(o, e.currentTarget as HTMLButtonElement);
    });
  }

  /** Endzustand nach dem Aufheben, wenn das Vario-Listing weg ist: Karte weg, Wahl-Pane bleibt
   *  verborgen (es gibt nichts mehr zuzuordnen), klare Ansage statt LISTING_NOT_FOUND. */
  function zeigeAufhebenEndzustand(host: HTMLElement | null, art: 'entfernt'): void {
    if (state) state.matchedOffer = null;
    if (host) {
      host.hidden = false;
      host.innerHTML = `
        <div class="kls-meta">
          <div class="kls-choice-title">Zuordnung aufgehoben</div>
          <div class="kls-choice-desc" style="margin-top:4px;">
            Der Kaufland-Verkaufskanal wurde am Vario-Artikel ${art}. Dieses Fenster gehört zu einem Listing, das es
            nicht mehr gibt. Bitte den Artikel in Vario neu öffnen; für eine neue Zuordnung den Kaufland-Verkaufskanal
            am Artikel wieder anlegen.
          </div>
        </div>`;
    }
    setStatusBar(ctx.contentEl, 'success', 'Zuordnung aufgehoben.');
  }

  /** Dieselbe RPC und dasselbe Warten wie „Zuordnung aufheben" im Angebote-Dialog. */
  async function doUnassign(o: MatchedOffer, btn: HTMLButtonElement): Promise<void> {
    if (!state) return;
    btn.disabled = true;
    setStatusBar(ctx.contentEl, 'loading', 'Zuordnung wird aufgehoben …');
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_unassign_vario_listing', {
        _storefront: state.storefront,
        _id_unit: o.idUnit,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string | undefined) : (data as { job_id?: string } | null)?.job_id;
      if (!jobId) throw new Error('Kein job_id zurückgeliefert');
      const job = await awaitJob(ctx.supabase, jobId, 60_000);
      if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
      const host = ctx.contentEl.querySelector<HTMLElement>('#mode-matched');
      if (host) delete host.dataset['unit'];
      // Der Job LOESCHT das Vario-Listing am Kanal (seit 2026-08-02, `removed: true`); nur wenn
      // Vario das verweigert, ist es bloss deaktiviert. Nach einem Loeschen gibt es das Listing,
      // zu dem dieses Fenster gehoert, nicht mehr -- ein Neuladen liefe in LISTING_NOT_FOUND und
      // liesse die Karte mit Fehlerzeile stehen (Review-Fund). Deshalb Endzustand statt Neuladen.
      const ergebnis = (job.result ?? {}) as { removed?: boolean; disabled?: boolean };
      if (ergebnis.removed === true) {
        zeigeAufhebenEndzustand(host, 'entfernt');
        return;
      }
      const geladen = await load();
      if (!geladen.ok && geladen.code === 'LISTING_NOT_FOUND') {
        zeigeAufhebenEndzustand(host, 'entfernt');
        return;
      }
      if (geladen.ok) {
        // Erst NACH load(): das setzt die Statuszeile selbst zurueck.
        setStatusBar(ctx.contentEl, 'success', 'Zuordnung aufgehoben. Der Verkaufskanal am Artikel ist deaktiviert, das Listing kann neu zugeordnet werden.');
      }
    } catch (e) {
      setStatusBar(ctx.contentEl, 'error', `Aufheben fehlgeschlagen: ${(e as Error).message}`);
      btn.disabled = false;
    }
  }

  /** Knopf-Regel aus dem geladenen Zustand; DB- und DOM-frei in slot-mode-buttons.ts. */
  function aktuelleKnoepfe() {
    return bestimmeSlotKnoepfe({
      ean: state?.decideInput.ean ?? null,
      hasManufacturer: state?.decideInput.hasManufacturer,
      manufacturerLabel: state?.decideInput.manufacturer ?? null,
      isVariantParent: state?.isVariantParent === true,
    });
  }

  // Detail-Pane in den Configure-Block mounten. Guard via data-listing-id:
  // renderModeUi() laeuft bei jedem Modus-Wechsel — das Pane soll nur neu
  // rendern, wenn sich die Inbox-Row geaendert hat (sonst verliert der User
  // Formular-/Tab-State). doAssign() loescht das Attribut explizit, damit
  // ein Kategorie-Wechsel ein frisches Pane erzwingt.
  function mountConfigurePane(): void {
    const paneEl = ctx.contentEl.querySelector<HTMLDivElement>('#configure-pane');
    if (!paneEl || !inboxListingId) return;
    if (paneEl.dataset['listingId'] === inboxListingId) return;
    paneEl.dataset['listingId'] = inboxListingId;
    void renderListingDetailPane(paneEl, {
      supabase: ctx.supabase,
      session: { token: ctx.session.token, tenantId: ctx.session.tenantId },
      supabaseUrl: ctx.supabaseUrl,
    }, inboxListingId, {
      kind: 'slot',
      // Nach jeder Aenderung im Pane (vor allem: nach dem Klick auf „Listen") den Status
      // NEU LADEN und NEU ZEICHNEN. Das Nachladen allein stand hier schon; gezeichnet
      // wurde es nie, und genau deshalb sah der Haendler nach dem Klick nichts.
      // Der Inbox-Status wird weiter nachgeladen — er steuert `renderModeUi` (Configure-Block
      // gegen Choice-Cards). Das frühere `renderCurrent()` entfällt mit der Box.
      onListingChanged: () => { void loadInboxStatus(); },
      onDeleted: () => {
        delete paneEl.dataset['listingId'];
        // Listing ist weg — zurueck zu den Choice-Cards (Kategorie neu zuweisen).
        forceChoice = true;
        void load();
      },
    });
  }

  function renderInputPreview() {
    const host = ctx.contentEl.querySelector<HTMLElement>('#input-preview');
    if (!host || !state) return;
    const { title, description, ean, manufacturer } = state.decideInput;
    const parts: string[] = [];
    if (title) parts.push(`<strong>Titel:</strong> ${escapeHtml(title)}`);
    if (ean) parts.push(`<strong>EAN:</strong> ${escapeHtml(ean)}`);
    // Variantenvater: keine EAN am Hauptartikel erwartet (siehe renderModeUi) — die
    // Kaufland-Produkte sind die Varianten, jede mit eigener EAN. Neutraler Hinweis
    // statt roter Fehlermeldung, sonst liest der Kunde einen Blocker, den es nicht gibt.
    else if (state.isVariantParent === true) parts.push(`<strong>EAN:</strong> <span class="v-text-muted">— nicht nötig, die EANs liegen an den Varianten</span>`);
    else parts.push(`<strong>EAN:</strong> <span class="v-text-error">— Bitte EAN in den Artikeldetails oder in diesem Listing weiter unten pflegen.</span>`);
    // Dieselbe Unterscheidung wie auf der Choice-Karte: ein verknuepftes Hersteller-Konto
    // ohne Label ist vorhanden, nicht fehlend (die Bezeichnung kommt beim Push aus dem Konto).
    if (manufacturer) parts.push(`<strong>Hersteller:</strong> ${escapeHtml(manufacturer)}`);
    else if (state.decideInput.hasManufacturer === true) parts.push(`<strong>Hersteller:</strong> <span class="v-text-muted">(Hersteller ohne Bezeichnung, Konto ist verknüpft)</span>`);
    else parts.push(`<strong>Hersteller:</strong> <span class="v-text-error">— Bitte Hersteller in den Artikeldetails pflegen.</span>`);
    if (description) parts.push(`<strong>Beschreibung:</strong> ${escapeHtml(description.slice(0, 200))}${description.length > 200 ? '…' : ''}`);
    if (parts.length === 0) { host.hidden = true; return; }
    host.hidden = false;
    host.innerHTML = parts.join('<br>');
  }

  /**
   * Zeichnet die eigene Kategorieliste im "Neu listen"-Pfad: Dropdown + Uebernehmen-Button,
   * oder -- falls der Haendler noch nichts ausgewaehlt hat -- einen Hinweis mit Verweis auf
   * die Einstellungen statt eines leeren Felds. Laedt nur einmal pro Slot-Aufruf (Cache in
   * eigeneKategorien); ein Fehlschlag zeigt eine Meldung statt den ganzen Ablauf zu blockieren.
   */
  async function renderEigeneKategorien(): Promise<void> {
    const emptyEl = ctx.contentEl.querySelector<HTMLElement>('#own-cat-empty')!;
    const pickerEl = ctx.contentEl.querySelector<HTMLElement>('#own-cat-picker')!;
    const errorEl = ctx.contentEl.querySelector<HTMLElement>('#own-cat-error')!;
    const selectEl = ctx.contentEl.querySelector<HTMLSelectElement>('#own-cat-select')!;
    const takeOverBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-own-cat-take-over')!;
    const subtitleEl = ctx.contentEl.querySelector<HTMLElement>('#own-cat-subtitle')!;

    // Kachelname wie in den Einstellungen ("Kaufland <Land>", siehe settings.ts
    // renderCategoryBlock/mp-name). Die frueher hier genannte eigene Karte
    // "Kaufland-Kategorien" gibt es seit Task 5 nicht mehr -- die Auswahl haengt jetzt an
    // der Storefront-Kachel dieses Listings, deshalb muss der Hinweis dorthin zeigen und
    // nicht generisch auf "Einstellungen" bleiben.
    const marktplatzName = state
      ? `Kaufland ${STOREFRONT_LABELS[state.storefront] ?? state.storefront.toUpperCase()}`
      : 'Kaufland';
    subtitleEl.textContent = `Aus deiner Auswahl in den Einstellungen, Kachel „${marktplatzName}".`;
    emptyEl.innerHTML = `Du hast noch keine eigenen Kategorien ausgewählt. Bitte zuerst in den ` +
      `Einstellungen in der Kachel <strong>${escapeHtml(marktplatzName)}</strong> auf ` +
      `„Kategorien wählen" klicken.`;

    if (eigeneKategorien === null && state) {
      try {
        eigeneKategorien = await ladeEigeneKategorien(ctx, state.storefront);
        errorEl.hidden = true;
      } catch (e) {
        console.warn('[listing-status] eigene Kategorien konnten nicht geladen werden:', (e as Error).message);
        errorEl.hidden = false;
        errorEl.textContent = `Eigene Kategorien konnten nicht geladen werden: ${(e as Error).message}`;
        emptyEl.hidden = true;
        pickerEl.hidden = true;
        takeOverBtn.disabled = true;
        return;
      }
    }

    const eigene = eigeneKategorien ?? [];
    if (eigene.length === 0) {
      emptyEl.hidden = false;
      pickerEl.hidden = true;
      takeOverBtn.disabled = true;
      return;
    }
    emptyEl.hidden = true;
    pickerEl.hidden = false;
    selectEl.innerHTML = eigene.map((k) => `<option value="${k.id}">${escapeHtml(k.name)}</option>`).join('');
    takeOverBtn.disabled = false;
  }

  /**
   * Nimmt eine Kategorie still in die eigene Liste auf, wenn sie dort noch fehlt -- fuer die
   * beiden Pfade, die der Haendler nicht selbst aus seiner Liste waehlt (EAN-Treffer im
   * Katalog, Kauflands Vorschlagsdienst). Manuell aus der eigenen Liste gewaehlte Kategorien
   * durchlaufen denselben Aufruf, er ist dort dann folgenlos.
   *
   * Fix-Runde 1, Befund 1: KEINE Vorabpruefung mehr gegen eigeneKategorien. Die urspruengliche
   * Fassung lud dafuer noetigenfalls den kompletten Storefront-Katalog nach (~700 KB) --
   * ausgerechnet im Aufschalt-Pfad (EAN-Treffer), der sonst gar keine Kategorieliste
   * anfasst, weil er die Kategorie fertig aus dem Kaufland-Katalog mitbringt. Die RPC ist
   * bei einer bereits vorhandenen category_id laut Migration 0039 folgenlos (kein Insert,
   * kein Zusatzjob) -- ein Read-vor-Write bringt hier also nichts, kostet aber ein volles
   * Katalog-Laden. Deshalb direkt schreiben statt vorher zu lesen.
   *
   * Blockiert den Ablauf nicht: Scheitert die RPC, wird trotzdem gelistet, und der Haendler
   * sieht die Kategorie spaeter nur nicht vorbelegt in seinem Dropdown -- deshalb wird jeder
   * Fehler hier nur geloggt, nie geworfen.
   */
  async function stelleKategorieSicher(categoryId: number): Promise<void> {
    // Ohne geladenen State kennen wir die Storefront des Listings nicht -- die RPC
    // verlangt sie seit Migration 0043 als ersten Pflichtparameter. Ohne state waere hier
    // ohnehin noch nichts zugewiesen worden, ein Abbruch ist also folgenlos.
    if (!state) return;
    try {
      // 'auto' fuegt nur hinzu und loescht nie, die bewusste Auswahl des Haendlers bleibt
      // unangetastet (siehe kaufland_set_tenant_categories, Migration 0039/0043). Storefront
      // stammt aus state.storefront -- derselben Quelle, die auch loadLiveStatus() und
      // doAssign() fuer den Storefront dieses Listings nutzen (vario-listing-category
      // action=load leitet sie serverseitig aus dem Vario-Verkaufskanal des Listings ab).
      const { error } = await ctx.supabase.rpc('kaufland_set_tenant_categories', {
        _storefront: state.storefront,
        _category_ids: [categoryId],
        _added_by: 'auto',
      });
      if (error) {
        console.warn('[listing-status] Kategorie konnte nicht automatisch aufgenommen werden:', error.message);
        return;
      }
      // Cache verwerfen, falls schon geladen -- der naechste Besuch von "Neu listen" zeigt
      // eine gerade aufgenommene Kategorie sofort mit an, statt den alten Stand zu behalten.
      eigeneKategorien = null;
    } catch (e) {
      console.warn('[listing-status] Kategorie-Abgleich fehlgeschlagen:', (e as Error).message);
    }
  }

  // ---------------------------------------------------------------------
  // Event-Wiring
  // ---------------------------------------------------------------------
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-reassign')!.addEventListener('click', () => {
    forceChoice = true;
    mode = 'choice';
    renderModeUi();
  });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup')!.addEventListener('click', () => void doLookup());
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-new-listing')!.addEventListener('click', () => { mode = 'new-listing'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-back-to-choice-1')!.addEventListener('click', () => { lookupResult = null; mode = 'choice'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-back-to-choice-2')!.addEventListener('click', () => { mode = 'choice'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-decide')!.addEventListener('click', () => void doDecide());
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-take-over')!.addEventListener('click', () => {
    const b = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-take-over')!;
    const idCat = Number(b.dataset['kcat']);
    if (Number.isFinite(idCat) && idCat > 0) void doAssign(idCat, b, b.dataset['kcatname'] ?? null);
  });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-new-instead')!.addEventListener('click', () => { lookupResult = null; mode = 'new-listing'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-own-cat-take-over')!.addEventListener('click', () => {
    const sel = ctx.contentEl.querySelector<HTMLSelectElement>('#own-cat-select')!;
    const id = Number(sel.value);
    if (!Number.isFinite(id) || id <= 0) return;
    const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-own-cat-take-over')!;
    const name = eigeneKategorien?.find((k) => k.id === id)?.name ?? null;
    void doAssign(id, btn, name);
  });

  // ---------------------------------------------------------------------
  async function doLookup() {
    if (!state?.decideInput.ean) return;
    mode = 'lookup';
    lookupResult = null;
    renderModeUi();
    const resultHost = ctx.contentEl.querySelector<HTMLElement>('#lookup-result')!;
    const eanRaw = state.decideInput.ean.trim();
    if (!isValidGtin(eanRaw)) {
      resultHost.innerHTML = `
        <div class="v-alert v-alert--warn">
          <strong>EAN ungültig.</strong> „${escapeHtml(eanRaw)}" ist keine gültige EAN/GTIN
          (erlaubt: 8, 12, 13 oder 14 Ziffern mit korrekter Prüfziffer).<br>
          Bitte die EAN am Vario-Artikel korrigieren und danach erneut suchen.
        </div>`;
      return;
    }
    resultHost.innerHTML = `<div class="v-text-muted v-pa-sm">Suche im Kaufland-Katalog nach EAN ${escapeHtml(state.decideInput.ean)} …</div>`;
    // v2-Slug: MCP-Deploy auf den alten Slug failt aktuell mit "internal error".
    // v2 enthaelt zusaetzlich Kategorie-Cache-Resolve + productUrl.
    const res = await callFn<FindByEanResponse>('kaufland-find-product-by-ean-v2', {
      storefront: state.storefront,
      ean: state.decideInput.ean,
    });
    if (!res.ok) {
      resultHost.innerHTML = `<div class="v-text-error v-pa-sm">Lookup fehlgeschlagen: ${escapeHtml(res.error)}</div>`;
      return;
    }
    const v = res.value;
    lookupResult = v;
    if (!v.found) {
      // „Neu listen" nur anbieten, wenn die Knopf-Regel es erlaubt; sonst den Grund nennen,
      // damit der Haendler nicht vor einem gesperrten Knopf ohne Erklaerung steht.
      const k = aktuelleKnoepfe();
      const weiter = k.newListing.enabled
        ? 'Du kannst den Artikel neu listen — wir schlagen eine Kategorie vor.'
        : `Neu listen ist gesperrt: ${escapeHtml(k.newListing.title)}`;
      resultHost.innerHTML = `
        <div class="v-alert v-alert--info">
          <strong>Kein Treffer.</strong> Diese EAN ist nicht im Kaufland-Katalog.<br>
          ${weiter}
        </div>`;
      return;
    }
    const catLabel = v.category?.title_singular ?? v.category?.name ?? null;
    const catCell = catLabel
      ? `${escapeHtml(catLabel)}${v.category?.level != null ? ` <span style="opacity:0.7;">· Level ${v.category.level}</span>` : ''} <code style="opacity:0.7;">(ID ${v.idCategory})</code>`
      : (v.idCategory ? `<code>ID ${v.idCategory}</code> <em style="opacity:0.7;">(Cache leer — Kategorie aktualisieren)</em>` : '—');
    const takeOverBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-take-over')!;
    takeOverBtn.dataset['kcat'] = String(v.idCategory ?? '');
    takeOverBtn.dataset['kcatname'] = catLabel ?? '';
    takeOverBtn.disabled = !v.idCategory;
    const openBtn = v.productUrl
      ? ` <a href="${escapeHtml(v.productUrl)}" target="_blank" rel="noopener noreferrer" class="v-icon-only" title="Im Kaufland-Katalog oeffnen" aria-label="Im Kaufland-Katalog oeffnen" style="margin-left:0.4em;color:inherit;text-decoration:none;">
          <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>
        </a>`
      : '';
    resultHost.innerHTML = `
      <div class="v-alert v-alert--ok">
        <strong>Artikel im Kaufland-Katalog gefunden.</strong>
      </div>
      <table class="v-table" style="width:100%;font-size:0.9em;margin-top:0.5em;">
        <tbody>
          <tr><th style="text-align:left;width:35%;">Kaufland-Produkt</th><td>${escapeHtml(v.title ?? '—')}</td></tr>
          <tr><th style="text-align:left;">Produkt-ID</th><td><code>${escapeHtml(v.idProduct ?? '')}</code>${openBtn}</td></tr>
          <tr><th style="text-align:left;">Kategorie</th><td>${catCell}</td></tr>
        </tbody>
      </table>
      <div style="font-size:0.85em;color:var(--v-color-text-muted,#666);margin-top:0.4em;">
        Aufschalten uebernimmt die Kaufland-Kategorie und schreibt sie an dein Vario-Listing zurueck.
      </div>
    `;
  }

  async function doDecide() {
    if (!state) return;
    const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-decide')!;
    btn.disabled = true;
    setStatusBar(ctx.contentEl, 'loading', 'Frage Kaufland nach Vorschlaegen …');
    ctx.contentEl.querySelector<HTMLElement>('#suggestions')!.innerHTML = '';
    try {
      const { title, description, price, manufacturer } = state.decideInput;
      const payload: Record<string, unknown> = {
        storefront: state.storefront,
        title,
        description,
        price,
      };
      if (manufacturer) payload['manufacturer'] = manufacturer;
      // Kanonischer Slug. Der frühere '-v3' stammt aus goe-connect, wo ein Deploy-Lock
      // auf dem alten Slug lag; auf diesem Projekt existiert er nicht und der
      // CORS-Preflight lief in 404 -> "Failed to fetch". Inhalt von v3 (manufacturer
      // pflicht, brand bewusst nicht gesendet) steckt in dieser Function.
      const res = await callFn<DecideResponse>('kaufland-decide-category', payload);
      if (!res.ok) {
        setStatusBar(ctx.contentEl, 'error', `Vorschlaege fehlgeschlagen: ${res.error}`);
        return;
      }
      renderSuggestions(res.value.suggestions);
      if (res.value.suggestions.length === 0) {
        setStatusBar(ctx.contentEl, 'error', 'Kaufland hat keine Vorschlaege geliefert — bitte eine Kategorie aus deiner eigenen Liste waehlen.');
      } else {
        setStatusBar(ctx.contentEl, '', '');
      }
    } finally {
      btn.disabled = false;
    }
  }

  function renderSuggestions(suggestions: DecideResponse['suggestions']) {
    const c = ctx.contentEl.querySelector<HTMLElement>('#suggestions')!;
    if (suggestions.length === 0) { c.innerHTML = '<div class="v-text-muted v-pa-sm">Keine Vorschlaege.</div>'; return; }
    c.innerHTML = suggestions.map((s) => {
      const conf = s.confidence != null ? `${Math.round(Number(s.confidence) * 100)}%` : '—';
      const name = s.title_singular ?? s.name ?? `#${s.kauflandCategoryId}`;
      return `
        <div style="display:flex;align-items:center;gap:0.6em;padding:0.5em;border-bottom:1px solid var(--v-color-border-soft,#eee);">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;">${escapeHtml(name)}</div>
            <div style="font-size:0.78em;color:var(--v-color-text-muted,#666);">Kaufland-ID ${s.kauflandCategoryId}${s.level != null ? ` · Level ${s.level}` : ''} · Confidence ${conf}</div>
          </div>
          <button type="button" class="v-button" data-kcat="${s.kauflandCategoryId}" data-kcatname="${escapeHtml(name)}">
            <span class="v-icon fa-regular fa-check"></span> Uebernehmen
          </button>
        </div>`;
    }).join('');
    c.querySelectorAll<HTMLButtonElement>('button[data-kcat]').forEach((b) => {
      b.addEventListener('click', () => { const id = Number(b.dataset['kcat']); void doAssign(id, b, b.dataset['kcatname'] ?? null); });
    });
  }

  function rufeZuweisung(kauflandCategoryId: number) {
    return callFn<{ ok: boolean; varioCategoryId: string }>('vario-listing-category', {
      action: 'assign',
      articleId: params.articleId,
      listingId: params.listingId,
      kauflandCategoryId,
    });
  }

  /**
   * Bringt eine Kategorie in den Vario-Kategoriebaum der Storefront.
   *
   * Warum das noetig ist: `vario-listing-category` (assign) sucht die Kategorie im Vario-Baum
   * der Storefront. Den Baum fuellt nur der Worker-Job `kaufland.push_categories_to_vario`, und
   * zwar mit den Kategorien aus der Auswahl des Haendlers (kaufland.tenant_categories) plus
   * Elternkette. Eine Kategorie aus dem Katalogtreffer oder dem Vorschlagsdienst steht dort
   * oft noch nicht; bis 2026-09-13 endete das in CATEGORY_NOT_PUSHED_TO_VARIO, und
   * stelleKategorieSicher nahm die Kategorie erst NACH einer erfolgreichen Zuweisung auf,
   * ohne den Sync anzustossen -- der Haendler musste in die Einstellungen.
   *
   * Ablauf: (1) steht die Kategorie schon in der Auswahl, fehlt nur der Sync; sonst Frage
   * „nur dieser Kanal oder alle aktiven" und Aufnahme als 'manual' (bei „alle" ueber dieselbe
   * Vererbungs-RPC wie das Kategorie-Modal). (2) Sync fuer die Quelle und jeden Kanal mit
   * Neuaufnahme anstossen (RPC ist idempotent). (3) auf den Job warten. true = fertig, die
   * Zuweisung kann wiederholt werden; false = abgebrochen oder gescheitert, die Statuszeile
   * traegt den Grund.
   */
  async function kategorieInVarioBaumBringen(kauflandCategoryId: number, displayName: string | null): Promise<boolean> {
    if (!state) return false;
    const storefront = state.storefront;

    let gewaehlt: GewaehlteKategorie[] = [];
    try {
      gewaehlt = await ladeGewaehlteKategorien(ctx.supabase, storefront);
    } catch (e) {
      setStatusBar(ctx.contentEl, 'error', `Kategorie-Auswahl nicht lesbar: ${(e as Error).message}`);
      return false;
    }
    const drin = gewaehlt.some((g) => g.categoryId === kauflandCategoryId);

    let vererbt: VererbungsErgebnis | null = null;
    if (!drin) {
      setStatusBar(ctx.contentEl, '', '');
      const wahl = await frageKanalwahl(kauflandCategoryId, displayName, storefront);
      if (!wahl) {
        setStatusBar(ctx.contentEl, 'error', 'Zuordnung abgebrochen, die Kategorie wurde nicht aufgenommen.');
        return false;
      }
      setStatusBar(ctx.contentEl, 'loading', 'Kategorie wird in die Auswahl aufgenommen …');
      // 'manual', weil der Haendler sich ausdruecklich entschieden hat. ACHTUNG: Die RPC ist
      // ein ABGLEICH und loescht jede manuelle Zeile der Storefront, die nicht in der Liste
      // steht (Migration 0043). Deshalb gehen alle bestehenden manuellen Zeilen mit
      // (kategorieIdsFuerAufnahme); nur die neue ID zu senden haette die gepflegte Auswahl
      // geloescht (Review-Fund 2026-09-13).
      const { error: setErr } = await ctx.supabase.rpc('kaufland_set_tenant_categories', {
        _storefront: storefront,
        _category_ids: kategorieIdsFuerAufnahme(gewaehlt, kauflandCategoryId),
        _added_by: 'manual',
      });
      if (setErr) {
        setStatusBar(ctx.contentEl, 'error', aufnahmeFehlerText(setErr.message, storefront));
        return false;
      }
      eigeneKategorien = null;
      if (wahl === 'alle') {
        // Dieselbe RPC wie „Vererben" im Kategorie-Modal: alle anderen Kanaele des Haendlers,
        // Kanaele ohne diese Kategorie im Katalog werden uebersprungen. Fail-soft: die Quelle
        // ist aufgenommen, ein Fehler hier darf die Zuordnung nicht kippen.
        const { data, error: vErr } = await ctx.supabase.rpc('kaufland_inherit_tenant_categories', {
          _from_storefront: storefront,
          _category_ids: [kauflandCategoryId],
        });
        if (vErr) console.warn('[listing-status] Vererben auf andere Kanaele fehlgeschlagen:', vErr.message);
        else vererbt = (data ?? {}) as VererbungsErgebnis;
      }
    }

    // Vererbte Kanaele nur anstossen, nicht abwarten: dort haengt keine Zuweisung dran, und
    // die RPC ist idempotent. Fehler dort sind Warnungen, die Quelle zaehlt.
    for (const sf of zielKanaeleFuerPush(storefront, vererbt)) {
      if (sf === storefront) continue;
      const { error } = await ctx.supabase.rpc('kaufland_enqueue_category_push', { _storefront: sf });
      if (error) console.warn('[listing-status] Kategorien-Sync fuer', sf, 'nicht gestartet:', error.message);
    }

    setStatusBar(ctx.contentEl, 'loading', `Kategorie wird in den Vario-Kategoriebaum ${storefront.toUpperCase()} übernommen …`);
    return pushAbwarten(storefront);
  }

  /**
   * Startet den Sync fuer die Storefront und wartet auf einen Lauf, der die AKTUELLE Auswahl
   * gelesen hat. Der Worker liest die Auswahl beim Job-Start; ein Job, der schon lief, als wir
   * kamen, kennt die eben aufgenommene Kategorie also nicht, und die RPC meldet fuer ihn nur
   * `already-running` ohne Job-ID (sie zaehlt queued UND running). Dann warten wir ueber
   * `active_job_id` aus kaufland_get_category_status (genau der offene Push-Job dieser
   * Storefront; `run_status` dort stammt auch von Pull-Laeufen und taugt nicht als Kriterium)
   * auf sein Ende und reihen nach. Hoechstens drei Runden, damit eine dauernd belegte Queue
   * nicht endlos haelt.
   */
  async function pushAbwarten(storefront: string): Promise<boolean> {
    for (let runde = 1; runde <= 3; runde += 1) {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_category_push', { _storefront: storefront });
      if (error) {
        setStatusBar(ctx.contentEl, 'error', `Kategorien-Sync konnte nicht gestartet werden: ${error.message}`);
        return false;
      }
      const zeile = pushZeile(data);
      if (zeile?.jobId) {
        try {
          const job = await awaitJob(ctx.supabase, zeile.jobId, { timeoutMs: SYNC_WARTEZEIT_MS, pollIntervalMs: 1500 });
          if (job.status !== 'completed') {
            setStatusBar(ctx.contentEl, 'error', describeJobFailure(job.error ?? job.status, 'Kategorien-Sync').message);
            return false;
          }
          return true;
        } catch (e) {
          setStatusBar(ctx.contentEl, 'error', `${describeJobFailure(e, 'Kategorien-Sync').message} Danach die Kategorie erneut übernehmen.`);
          return false;
        }
      }
      if (zeile?.reason !== 'already-running') {
        setStatusBar(ctx.contentEl, 'error', 'Kategorien-Sync wurde nicht angestoßen (keine Job-ID zurück).');
        return false;
      }
      const aktiv = await aktiverPushJob(storefront);
      if (aktiv) {
        try {
          await awaitJob(ctx.supabase, aktiv, { timeoutMs: SYNC_WARTEZEIT_MS, pollIntervalMs: 1500 });
        } catch (e) {
          setStatusBar(ctx.contentEl, 'error', `${describeJobFailure(e, 'Der bereits laufende Kategorien-Sync').message} Danach die Kategorie erneut übernehmen.`);
          return false;
        }
      } else {
        await new Promise((r) => setTimeout(r, 2000));
      }
    }
    setStatusBar(ctx.contentEl, 'error', 'Der Kategorien-Sync kam nicht zum Zug, es lief immer schon einer. Bitte in den Einstellungen abwarten und die Kategorie danach erneut übernehmen.');
    return false;
  }

  /** Job-ID des offenen Push-Jobs der Storefront (queued oder running), sonst null. */
  async function aktiverPushJob(storefront: string): Promise<string | null> {
    const { data, error } = await ctx.supabase.rpc('kaufland_get_category_status');
    if (error) {
      console.warn('[listing-status] kaufland_get_category_status:', error.message);
      return null;
    }
    const rows = (data ?? []) as Array<{ storefront: string; active_job_id: string | null }>;
    return rows.find((r) => r.storefront === storefront)?.active_job_id ?? null;
  }

  /** Inline-Frage statt Browser-Dialog (window.confirm ist in dieser App verboten). */
  function frageKanalwahl(kauflandCategoryId: number, displayName: string | null, storefront: string): Promise<KanalWahl | null> {
    const host = ctx.contentEl.querySelector<HTMLElement>('#kategorie-frage');
    if (!host) return Promise.resolve(null);
    // Eine noch offene Frage (zweiter Zuweisungsklick) als Abbruch aufloesen, siehe offeneFrage.
    offeneFrage?.(null);
    offeneFrage = null;
    host.hidden = false;
    // kls-meta ist die Box des Slots; v-alert waere in keinem geladenen Stylesheet definiert.
    host.innerHTML = `
      <div class="kls-meta">
        <div>${escapeHtml(uebernahmeFrage({ kategorieName: displayName, kategorieId: kauflandCategoryId, storefront }))}</div>
        <div class="kls-actions-row" style="margin-top:8px;justify-content:flex-start;">
          <button type="button" class="kls-btn kls-btn--primary" data-wahl="alle">Für alle aktiven Kanäle übernehmen</button>
          <button type="button" class="kls-btn kls-btn--secondary" data-wahl="nur-diese">Nur für ${escapeHtml(storefront.toUpperCase())}</button>
          <button type="button" class="kls-btn kls-btn--secondary" data-wahl="abbrechen">Abbrechen</button>
        </div>
      </div>`;
    return new Promise((resolve) => {
      offeneFrage = resolve;
      host.querySelectorAll<HTMLButtonElement>('button[data-wahl]').forEach((b) => {
        b.addEventListener('click', () => {
          host.hidden = true;
          host.innerHTML = '';
          offeneFrage = null;
          const w = b.dataset['wahl'];
          resolve(w === 'alle' || w === 'nur-diese' ? w : null);
        });
      });
    });
  }

  async function doAssign(kauflandCategoryId: number, btn: HTMLButtonElement, displayName: string | null = null) {
    // Attach-Pfad: der User kommt aus dem Lookup-Treffer, die Kaufland-id_product ist bekannt.
    // Neu-Listen-Pfad: lookupResult null oder not-found. Die Unterscheidung steuert unten den
    // Upsert-Modus und hier den Guard.
    const isAttach = !!lookupResult?.found && !!lookupResult?.idProduct;
    // Guard zur Knopf-Regel (2026-09-13): Neu listen braucht EAN und Hersteller. Die Knoepfe
    // sind gesperrt, aber die Regel darf nicht nur an Knoepfen haengen -- ein zweiter Weg in
    // diesen Pfad („Doch neu listen" im Lookup) war schon einmal offen.
    if (!isAttach) {
      const k = aktuelleKnoepfe();
      if (!k.newListing.enabled) {
        setStatusBar(ctx.contentEl, 'error', `Neu listen nicht möglich: ${k.newListing.title}`);
        return;
      }
    }
    setStatusBar(ctx.contentEl, 'loading', 'Uebernehme Kategorie …');
    btn.disabled = true;
    let res = await rufeZuweisung(kauflandCategoryId);
    let repariert = false;
    if (!res.ok) {
      // Kategorie fehlt im Vario-Baum der Storefront, oder es gibt noch keinen Baum. Das ist
      // kein Fehler des Haendlers, sondern eine noch nicht uebernommene Kategorie (Anlass
      // 2026-09-13: CATEGORY_NOT_PUSHED_TO_VARIO fuer 40731 beim Aufschalten). Aufnehmen,
      // Sync starten, warten, die Zuweisung EINMAL wiederholen.
      const syncFehler = erkenneKategorieSyncFehler(res.code ?? res.error);
      if (syncFehler) {
        repariert = await kategorieInVarioBaumBringen(kauflandCategoryId, displayName);
        if (!repariert) { btn.disabled = false; return; }
        setStatusBar(ctx.contentEl, 'loading', 'Uebernehme Kategorie …');
        res = await rufeZuweisung(kauflandCategoryId);
      }
    }
    if (!res.ok) {
      // Nach Aufnahme und Sync darf hier nicht wieder „Kategorien-Sync ausfuehren" stehen,
      // genau das ist gerade passiert. Dann fehlt der Knoten aus einem anderen Grund (etwa vom
      // Push uebersprungen, weil die Elternkategorie im Katalog fehlt; Worker-Log).
      setStatusBar(ctx.contentEl, 'error', repariert
        ? `Kategorie aufgenommen und Kategorien-Sync gelaufen, Vario kennt den Knoten trotzdem nicht: ${res.error}`
        : `Zuweisung fehlgeschlagen: ${res.error}`);
      btn.disabled = false;
      return;
    }

    // Fremde Kategorie (EAN-Treffer im Katalog oder Kauflands Vorschlagsdienst) still in die
    // eigene Liste aufnehmen, falls sie dort noch fehlt. Bei der manuellen Auswahl aus der
    // eigenen Liste ist der Aufruf folgenlos, weil die Kategorie dort schon steht.
    // Nach einer Reparatur steht die Kategorie bereits als 'manual' in der Auswahl; der stille
    // 'auto'-Abgleich wuerde nur einen zweiten Attribut-Refresh einreihen.
    if (!repariert) await stelleKategorieSicher(kauflandCategoryId);

    // Lister-Inbox direkt schreiben — wir kennen alle Daten aus state + assign-response.
    // Kein scan-roundtrip noetig. await damit das anschliessende load() den frisch
    // geschriebenen inboxStatus sieht und die Ready-Box korrekt einblendet; fail-soft
    // bleibt (UI-Erfolg ist schon gesetzt, Upsert-Fehler nur geloggt). _kaufland_category_id
    // kommt direkt aus dem klick-aufruf — der mapping-editor (stufe 4) braucht das pflicht.
    //
    // Attach-Pfad: wenn der User aus dem Lookup-Treffer "Auf dieses Angebot aufschalten"
    // klickt, kennen wir die Kaufland-id_product aus dem letzten EAN-Lookup. Wir
    // schalten den Lister auf _mode='attach' und reichen _attach_id_product durch —
    // die RPC (Migration 0113) speichert das am Listing, damit der Push spaeter den
    // Aufschalt-Flow statt Neu-Anlage waehlt. Neu-Listen-Pfad (lookupResult null oder
    // not-found) bleibt bei den RPC-Defaults (_mode='new', _attach_id_product=null).
    // `isAttach` steht oben am Funktionsanfang, weil der Guard es ebenfalls braucht.
    if (state) {
      const upsertArgs: Record<string, unknown> = {
        _storefront: state.storefront,
        _vario_article_id: params.articleId,
        _vario_listing_id: params.listingId,
        _vario_category_id: res.value.varioCategoryId,
        _kaufland_category_id: kauflandCategoryId,
        _vario_channel_id: state.salesChannelId,
        _vario_article_name: state.decideInput.title || null,
        _vario_article_number: state.articleNumber,
        _ean: state.decideInput.ean,
        _description: state.decideInput.description,
        _category_name: displayName,
        _is_variant_parent: state.isVariantParent ?? false,
      };
      if (isAttach && lookupResult?.idProduct) {
        upsertArgs['_mode'] = 'attach';
        upsertArgs['_attach_id_product'] = lookupResult.idProduct;
      }
      const { error: upsertError } = await ctx.supabase.rpc('kaufland_upsert_listing_from_slot', upsertArgs);
      if (upsertError) console.warn('[listing-status] upsert listing failed:', upsertError.message);
    }

    setStatusBar(
      ctx.contentEl,
      'success',
      isAttach
        ? 'Kategorie übernommen — Aufschalt-Konditionen unten ausfüllen.'
        : 'Kategorie zugeordnet — Listing unten konfigurieren.',
    );
    ctx.contentEl.querySelector<HTMLElement>('#suggestions')!.innerHTML = '';
    ctx.contentEl.querySelector<HTMLElement>('#lookup-result')!.innerHTML = '';
    mode = 'choice';
    forceChoice = false; // Configure-Block wieder einblenden, Choice-Cards weg.
    // Kategorie hat sich geaendert — Configure-Pane beim naechsten Mount frisch rendern.
    const paneEl = ctx.contentEl.querySelector<HTMLDivElement>('#configure-pane');
    if (paneEl) delete paneEl.dataset['listingId'];
    await load();
  }

  async function callFn<T>(name: string, body: unknown): Promise<{ ok: true; value: T } | { ok: false; error: string; code?: string | undefined }> {
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let parsed: unknown;
      try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
      if (!res.ok) {
        const errBag = (parsed as { error?: { code?: string; message?: string; body?: string } } | null)?.error;
        const msg = errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}${errBag.body ? ` — ${errBag.body}` : ''}` : `HTTP ${res.status}: ${text.slice(0, 500)}`;
        // `code` zusaetzlich roh, damit Aufrufer auf den Fehlercode verzweigen koennen, statt
        // den formatierten Text zu parsen (kategorieInVarioBaumBringen).
        return { ok: false, error: msg, code: errBag?.code };
      }
      return { ok: true, value: parsed as T };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function renderShell(): string {
  return `
    <style>
      /* Listing-status-Slot = Fenster mit internem Scroll. Der Vario-Artikel-Bereich
         ist kuerzer als lange Tabs (Mapping, Bilder, Decide) — bei iframe=Inhaltshoehe
         ragte der untere Teil aus dem sichtbaren Vario-Panel und war unerreichbar.
         Daher: der GESAMTE Slot-Inhalt scrollt in .kls-shell, egal wie hoch der
         einzelne Tab ist. Die iframe-Hoehe selbst setzt seit 2026-08-01 Vario
         (vario-iframe.ts requestVarioFullHeight), frueher war es ein Festwert.
         html/body/main auf 100% klemmen, damit .kls-shell der einzige Scroll-
         Container ist (bridge.css setzt diese ohnehin auf overflow:hidden). */
      html, body { height: 100% !important; margin: 0 !important; overflow: hidden !important; }
      main#app, main#app > section#content { height: 100% !important; overflow: hidden !important; }
      /* Verbindungs-Punkt der App-Shell (patterns.css .status-dot, position:absolute
         oben rechts) ausblenden — der Slot hat seine eigene Statuszeile (#slot-status),
         der Punkt klebte sonst ueber dem Slot-Header. */
      #status-dot { display: none !important; }

      /* Slot-Window-Design analog Brand-Layer (kl-modal / kl-form-row / v-banner). */
      .kls-shell {
        display: flex; flex-direction: column; gap: 12px;
        padding: 16px;
        font-family: 'Outfit', system-ui, sans-serif;
        color: #101828;
        /* DER eine Scroll-Container des Slots: fuellt die feste iframe-Hoehe und
           scrollt den gesamten Inhalt (Header + Aktionen + Configure-Pane).
           100vh statt 100% — unabhaengig von der html/body/main-height-Kette.
           box-sizing explizit, da das Frontend keinen globalen border-box-Reset hat,
           sonst sprengt das 16px-Padding die 100vh. */
        height: 100vh; box-sizing: border-box; overflow-y: auto;
      }
      .kls-head {
        display: flex; align-items: center;
        gap: 14px;
      }
      .kls-head h3 {
        margin: 0; font-size: 21px; font-weight: 700; letter-spacing: -0.015em;
        line-height: 1.15; color: #1A1A1A;
      }
      /* Der Status rutscht ans rechte Ende, seit links Marke und Titel stehen. Er behaelt
         den auto-Rand, auch wenn der Knopf noch weiter rechts steht: setStatusBar leert
         #slot-status nur, entfernt es nie. Der auto-Rand traegt also immer. */
      .kls-head #slot-status { margin-left: auto; }
      .kls-head #btn-reassign { flex: none; }
      /* Das hidden-Attribut verliert gegen ein display aus einer Klasse (Projektregel).
         .kls-btn setzt display:inline-flex, der Knopf staende also auch ausserhalb des
         Configure-Modus da. Der Guard gilt fuer den gesamten Slot, damit derselbe
         Fallstrick nicht beim naechsten Toggle-Element erneut zuschlaegt. */
      .kls-shell [hidden] { display: none !important; }
      .kls-head .brand { display: inline-flex; align-items: center; text-decoration: none; flex: none; }
      .kls-head .brand svg { height: 19px; width: auto; display: block; }
      .kls-head .kf-logo { display: inline-flex; flex: none; }
      .kls-head .kf-logo svg { height: 28px; width: auto; display: block; }
      .kls-brand-divider {
        width: 1px; height: 24px; background: #E4E4E7; flex: none;
      }
      .kls-brand-eyebrow {
        font-size: 10.5px; font-weight: 600; letter-spacing: .12em;
        text-transform: uppercase; color: #737373;
      }
      .kls-subtitle { font-size: 12px; color: #475467; margin-top: 2px; }

      .kls-section-title {
        font-weight: 600; font-size: 13px; color: #101828;
        margin-bottom: 6px;
      }
      .kls-meta {
        background: #F9FAFB;
        border: 1px solid #E4E7EC;
        border-radius: 8px;
        padding: 10px 12px;
        font-size: 12.5px;
      }
      .kls-meta table { width: 100%; border-collapse: collapse; }
      .kls-meta th {
        text-align: left; width: 38%; padding: 3px 0;
        font-weight: 500; color: #667085;
      }
      .kls-meta td { padding: 3px 0; }
      .kls-meta code {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 12px; color: #1849A9;
      }

      .kls-choice-card {
        border: 1px solid #E4E7EC;
        border-radius: 8px;
        padding: 12px 14px;
        background: #fff;
        display: flex; flex-direction: column; gap: 8px;
        transition: border-color 140ms ease, box-shadow 140ms ease;
      }
      .kls-choice-card:hover {
        border-color: #D0D5DD;
        box-shadow: 0 1px 4px rgba(16,24,40,0.06);
      }
      .kls-choice-title { font-weight: 600; font-size: 13.5px; }
      .kls-choice-desc { font-size: 12px; color: #475467; line-height: 1.45; }
      .kls-choice-actions { display: flex; justify-content: flex-end; }

      .kls-mode-head {
        display: flex; align-items: center; gap: 8px;
        margin-bottom: 10px;
      }
      .kls-mode-head strong { font-size: 13.5px; }
      .kls-back-btn {
        width: 28px; height: 28px;
        display: inline-flex; align-items: center; justify-content: center;
        background: transparent; border: 1px solid transparent;
        border-radius: 6px; cursor: pointer; color: #475467;
        transition: border-color 140ms ease, background 140ms ease;
      }
      .kls-back-btn:hover { border-color: #D0D5DD; background: #F9FAFB; }
      .kls-back-btn .v-icon { font-size: 12px; }

      .kls-actions-row {
        display: flex; justify-content: flex-end; gap: 8px;
        margin-top: 12px; flex-wrap: wrap;
      }
      .kls-actions-row.split { justify-content: space-between; }

      .kls-btn {
        display: inline-flex; align-items: center; gap: 6px;
        height: 32px; padding: 0 14px;
        border-radius: 6px; cursor: pointer; font: inherit; font-size: 12.5px;
        transition: filter 140ms ease, background 140ms ease, border-color 140ms ease;
      }
      .kls-btn--primary {
        background: linear-gradient(180deg, #1A1D21 0%, #0D0F11 100%);
        color: #fff; border: 1px solid #0D0F11;
        box-shadow: 0 1px 2px rgba(26,29,33,0.20);
      }
      .kls-btn--primary:hover { filter: brightness(0.96); }
      .kls-btn--primary[disabled] { opacity: 0.55; cursor: not-allowed; filter: none; }
      .kls-btn--secondary {
        background: #fff; color: #344054; border: 1px solid #D0D5DD;
      }
      .kls-btn--secondary:hover { border-color: #98A2B3; background: #F9FAFB; }
      .kls-btn--secondary[disabled] { opacity: 0.55; cursor: not-allowed; background: #fff; border-color: #D0D5DD; }
      /* Destruktive Bestaetigung (Zuordnung aufheben): rot abgesetzt wie der Angebote-Dialog. */
      .kls-btn--danger { background: #fff; color: #B42318; border: 1px solid #FDA29B; }
      .kls-btn--danger:hover { background: #FEF3F2; border-color: #F97066; }
      .kls-btn--danger[disabled] { opacity: 0.55; cursor: not-allowed; }
      .kls-btn--ghost {
        background: transparent; color: #475467; border: 1px solid transparent;
      }
      .kls-btn--ghost:hover { border-color: #D0D5DD; background: #F9FAFB; }

      .kls-banner {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        border: 1px solid transparent;
        font-size: 12.5px; line-height: 1.45;
      }
      .kls-banner--ok { background: #E6F4EA; border-color: #ABEFC6; color: #15803D; }
      .kls-banner--info { background: #EFF4FF; border-color: #B2CCFF; color: #1849A9; }
      .kls-banner .v-icon { font-size: 14px; margin-top: 1px; }

      .kls-error-text {
        font-size: 11.5px; color: #B42318; margin-top: 4px;
      }

      .kls-input-preview {
        padding: 10px 12px;
        background: #fff;
        border: 1px dashed #D0D5DD;
        border-radius: 8px;
        font-size: 12.5px; line-height: 1.45;
        color: #475467;
      }

      .kls-suggestions-head {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 6px;
      }
      .kls-suggestions-head strong { font-size: 13px; }
    </style>

    <div class="kls-shell">

      <!-- Markenzeile aus dem Entwurf „Kaufland Kategorie.html". Sie steht HIER, im Kopf
           des Slots, und nicht im Detailfenster darunter: Sie beschreibt die App, nicht das
           einzelne Listing. Die Storefront-Angabe fuellt setSubtitle wie bisher — sie hat
           nur einen neuen Platz, keine neue Quelle. Logos als Inline-SVG aus dem
           Design-Paket, nicht als Bilddatei. -->
      <div class="kls-head">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland">${KF_LOGO_KAUFLAND}</span>
        <span class="kls-brand-divider"></span>
        <div>
          <div id="slot-subtitle" class="kls-brand-eyebrow"></div>
          <h3>Kaufland Lister</h3>
        </div>
        <div id="slot-status"></div>
        <!-- „Kategorie aendern" steht in der Kopfzeile, nicht ueber dem Configure-Pane:
             Es ist die einzige Aktion des Configure-Modus und gehoert an den Rand, nicht
             in den Lesefluss. Sichtbar macht es renderModeUi() zusammen mit dem Pane —
             ausserhalb des Configure-Modus gibt es keine zugeordnete Kategorie, die man
             aendern koennte. -->
        <button type="button" class="kls-btn kls-btn--secondary" id="btn-reassign" hidden>
          <span class="v-icon fa-regular fa-pen-to-square"></span> Kategorie ändern
        </button>
      </div>

      <!-- Inline-Frage vor der Aufnahme einer Kategorie in die Auswahl (frageKanalwahl). Steht
           ausserhalb der Modus-Container, weil die Zuweisung aus dem Lookup UND aus Neu-listen
           kommt. Kein window.confirm: Browser-Dialoge sind in dieser App verboten. -->
      <div id="kategorie-frage" hidden></div>

      <div id="mode-configure" hidden>
        <div id="configure-pane" class="kls-configure-pane" style="border:1px solid #E4E7EC;border-radius:8px;background:#fff;"></div>
      </div>

      <!-- Angebots-Zuordnung ohne Posteingangszeile (renderMatchedKarte): Karte statt Wahl. -->
      <div id="mode-matched" hidden></div>

      <div id="mode-choice">
        <div class="kls-section-title">Was möchtest du tun?</div>

        <div style="display:flex;flex-direction:column;gap:10px;">
          <div class="kls-meta">
            <div style="font-weight:600;margin-bottom:6px;">Artikel-Daten aus Vario</div>
            <table>
              <tbody>
                <tr><th>EAN</th><td><code id="choice-ean">—</code></td></tr>
                <tr><th>Hersteller</th><td id="choice-manufacturer">—</td></tr>
              </tbody>
            </table>
            <div id="choice-no-manufacturer" hidden class="kls-error-text">
              Ohne Hersteller ist „Neu listen" gesperrt. Bitte Hersteller in den Artikeldetails pflegen.
            </div>
            <div id="choice-no-ean" hidden class="kls-error-text">
              Ohne EAN sind „Artikel suchen" und „Neu listen" gesperrt. Bitte EAN in den Artikeldetails oder in diesem Listing weiter unten pflegen.
            </div>
          </div>

          <div id="choice-variant-parent-note" hidden class="kls-meta">
            <div style="font-weight:600;margin-bottom:4px;">
              <span class="v-icon fa-regular fa-sitemap"></span> Variantenartikel
            </div>
            <div>
              Dieser Artikel ist ein Variantenvater. Er wird bei Kaufland nicht selbst als
              Produkt gelistet — jede Variante ist ein eigenes Kaufland-Produkt mit eigener
              EAN. Am Vater wird nur die Kategorie zugeordnet; die Varianten legst du danach
              im Reiter „Varianten“ an. Eine EAN am Vater ist nicht nötig.
            </div>
          </div>

          <div id="choice-card-attach" class="kls-choice-card">
            <div class="kls-choice-title">Auf bestehendes Angebot aufschalten</div>
            <div class="kls-choice-desc">Sucht den Artikel per EAN im Kaufland-Katalog. Treffer ⇒ Kategorie wird übernommen.</div>
            <div class="kls-choice-actions">
              <button type="button" class="kls-btn kls-btn--primary" id="btn-lookup">
                <span class="v-icon fa-regular fa-magnifying-glass"></span> Artikel suchen
              </button>
            </div>
          </div>

          <div class="kls-choice-card">
            <div class="kls-choice-title">Neu listen</div>
            <div class="kls-choice-desc">Kaufland kennt den Artikel noch nicht. Wir schlagen eine Kategorie vor (oder du suchst manuell).</div>
            <div class="kls-choice-actions">
              <button type="button" class="kls-btn kls-btn--secondary" id="btn-new-listing">
                <span class="v-icon fa-regular fa-plus"></span> Neu listen
              </button>
            </div>
          </div>
        </div>
      </div>

      <div id="mode-lookup" hidden>
        <div class="kls-mode-head">
          <button type="button" class="kls-back-btn" id="btn-back-to-choice-1" title="Zurück" aria-label="Zurück">
            <span class="v-icon fa-light fa-arrow-left"></span>
          </button>
          <strong>Kaufland-Katalog</strong>
        </div>
        <div id="lookup-result"></div>
        <div class="kls-actions-row split">
          <button type="button" class="kls-btn kls-btn--ghost" id="btn-lookup-new-instead">
            <span class="v-icon fa-regular fa-plus"></span> Doch neu listen
          </button>
          <button type="button" class="kls-btn kls-btn--primary" id="btn-lookup-take-over" disabled>
            <span class="v-icon fa-regular fa-check"></span> Auf dieses Angebot aufschalten
          </button>
        </div>
      </div>

      <div id="mode-new-listing" hidden>
        <div class="kls-mode-head">
          <button type="button" class="kls-back-btn" id="btn-back-to-choice-2" title="Zurück" aria-label="Zurück">
            <span class="v-icon fa-light fa-arrow-left"></span>
          </button>
          <strong>Neu listen</strong>
        </div>

        <div id="input-preview" hidden class="kls-input-preview" style="margin-bottom:10px;"></div>

        <div style="margin-bottom:12px;">
          <div class="kls-suggestions-head">
            <strong>Kaufland-Vorschläge</strong>
            <button type="button" class="kls-btn kls-btn--primary" id="btn-decide">
              <span class="v-icon fa-regular fa-lightbulb"></span> Vorschläge holen
            </button>
          </div>
          <div id="suggestions"></div>
        </div>

        <div>
          <strong style="font-size:13px;">Eigene Kategorien</strong>
          <div id="own-cat-subtitle" class="kls-subtitle" style="margin:2px 0 8px;">
            Aus deiner Auswahl in den Einstellungen.
          </div>
          <div id="own-cat-empty" hidden class="kls-subtitle">
            Du hast noch keine eigenen Kategorien ausgewählt.
          </div>
          <div id="own-cat-error" hidden class="kls-error-text"></div>
          <div id="own-cat-picker" hidden>
            <div style="display:flex;gap:8px;align-items:center;">
              <div class="v-select" style="flex:1;min-width:0;"><select id="own-cat-select"></select></div>
              <button type="button" class="kls-btn kls-btn--primary" id="btn-own-cat-take-over" disabled>
                <span class="v-icon fa-regular fa-check"></span> Übernehmen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function readSlotParams(): { articleId: string | null; listingId: string | null } {
  const q = new URL(window.location.href).searchParams;
  return {
    articleId: q.get('articleId') ?? q.get('article_id') ?? q.get('id'),
    listingId: q.get('listingId') ?? q.get('listing_id'),
  };
}

function setStatusBar(host: HTMLElement, kind: 'loading' | 'success' | 'error' | '', msg: string): void {
  const el = host.querySelector<HTMLElement>('#slot-status');
  if (!el) return;
  if (!kind) { el.innerHTML = ''; return; }
  const icon = kind === 'loading' ? 'fa-spinner-third fa-spin' : kind === 'success' ? 'fa-circle-check' : 'fa-circle-xmark';
  const cls = kind === 'success' ? 'v-text-success' : kind === 'error' ? 'v-text-error' : 'v-text-muted';
  el.innerHTML = `<span class="${cls}" style="font-size:0.88em;"><span class="v-icon fa-regular ${icon}"></span> ${escapeHtml(msg)}</span>`;
}

function setSubtitle(text: string): void {
  const el = document.querySelector<HTMLElement>('#slot-subtitle');
  if (el) el.textContent = text;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
